import radarDataApi from '../radar-data-api'
import { getDocument, upsertDocument, archiveDocument } from '../content-service-api'
import newsFeedApi from '../news-feed-api'
import { getPhenomena } from '../search-api'
import { map, find, filter } from 'lodash-es'

const mergePhenomena = (phenomena, phenomenonDocuments) =>
    phenomena
        .filter(phenomenon => Boolean(phenomenon.id))
        .map(phenomenon => {
            const searchApiResult = find(phenomenonDocuments, result => phenomenon.id === result.id)

            return searchApiResult
                ? {
                    ...phenomenon,
                    ...searchApiResult
                }
                : null
        })
        .filter(Boolean)

export const getRadarPhenomena = async (radarId, groupId) => {
    const { data: radarDataApiData } = await radarDataApi.getRadar(radarId)

    const uuids = map(radarDataApiData.phenomena, ({ id }) => id)
        .filter(Boolean)

    const { result: phenomenonDocuments = [] } = uuids.length
        ? await getPhenomena({ phenomena: uuids, groups: [0, Number(groupId)], size: uuids.length, include_archived: true })
        : {}

    let phenomena = mergePhenomena(radarDataApiData.phenomena, phenomenonDocuments)

    return {
        ...radarDataApiData,
        phenomena
    }
}

export const NEWSFEED_ERROR_PARTIAL = 'NEWSFEED_ERROR_PARTIAL'
export const NEWSFEED_ERROR = 'NEWSFEED_ERROR'
export const NEWSFEED_SUCCESS = 'NEWSFEED_SUCCESS'

export const archivePhenomenon = async (id, group) => await archiveDocument({ type: 'phenomenon', id, group })

export const storePhenomenon = async (phenomenon) => await upsertDocument({type: 'phenomenon', ...phenomenon})

export const storeNewsFeeds = async (phenomenon, newsFeedChanges) => {
  const { addedNewsFeeds, deletedNewsFeeds } = newsFeedChanges;

  // Execute delete operations first
  const deleteOperations = await Promise.all(
      deletedNewsFeeds.map(newsFeed =>
          newsFeedApi.removeNewsFeed(
              phenomenon.groups[0],
              newsFeed.id,
              phenomenon.id
          )
      )
  );

  // After delete operations, execute add operations
  const addedResults = await Promise.all(
      addedNewsFeeds.map(newsFeed =>
          newsFeedApi.addNewsFeed(
              phenomenon.groups[0],
              newsFeed?.url || newsFeed?.title,
              phenomenon?.id
          )
      )
  );

  // Collect failed news feed titles from add operations
  const failedNewsFeedTitles = addedResults
      .map((result, index) => (!result ? addedNewsFeeds[index].title : null))
      .filter(title => !!title);

  // Return appropriate status
  if (failedNewsFeedTitles.length) {
      return {
          status: NEWSFEED_ERROR_PARTIAL,
          failedNewsFeedTitles
      };
  }

  return {
      status: NEWSFEED_SUCCESS
  };
};

export const storePhenomenonWithNewsFeeds = async (phenomenon, newsFeedChanges) => {
    const storedPhenomenon = await storePhenomenon(phenomenon)

    try {
        const newsFeedStatus = await storeNewsFeeds(storedPhenomenon, newsFeedChanges)

        return {
            ...newsFeedStatus,
            storedPhenomenon
        }
    } catch (error) {
        return {
            status: NEWSFEED_ERROR,
            error,
            storedPhenomenon
        }
    }
}

export const getPhenomenonByUUIDAndGroup = async (id, group) => {
  const phenomenon = await getDocument({ group, id, type: 'phenomenon' })

  try {
    const uuids = phenomenon.content.related_phenomena
    let relatedPhenomena = []
    const newsFeeds = await newsFeedApi.getNewsFeeds(group, id) || []
    if (Array.isArray(uuids) && !!uuids?.length && uuids.length > 0) {
      const result = await getPhenomena({ phenomena: uuids, groups: [0, Number(group)], size: uuids.length })
      const relatedPhenomenaTemp = result?.result || []
      relatedPhenomena  = !!relatedPhenomenaTemp?.length && relatedPhenomenaTemp?.sort(function(a, b) {
        return uuids?.indexOf(a?.id) - uuids.indexOf(b?.id);
      });
    }

    return {
      ...phenomenon,
      newsFeeds,
      relatedPhenomena
    }
  } catch (error) {
    return { error }
  }
}

export function transformFromLegacy({ uuid, language, group, title, shortTitle, lead, video, image, imageFile, imageUrl, videoText, phenomenonType, body, relatedPhenomena, feedTag, timing, links }) {
  return {
    id: uuid,
    language,
    group: Number(group),
    content: {
      title,
      short_title: shortTitle,
      summary: lead,
      body,
      type: phenomenonType,
      media: { video: video || '', image: imageUrl || '', text: videoText  || '' },
      related_phenomena: relatedPhenomena.map(({ id }) => id),
      feed_tags: Array.isArray(feedTag) ? feedTag.map(({ id }) => id) : [],
      time_range: timing,
      links: Array.isArray(links) ? links.map(({ value }) =>({ url: value})) : []
    },
    _upload: {
      image,
      imageFile,
      imageUrl
    }
  }
}

export function transformToLegacy (
  {
    id: uuid,
    language,
    groups,
    content: {
      title = '',
      short_title: shortTitle = '',
      summary: lead = '',
      body = '',
      type,
      media: { video: videoUrl, image: imageUrl, text: mediaText } = {},
      related_phenomena: relatedPhenomena = [],
      feed_tags = [],
      time_range: timing = {},
      links
    } = {},
    ...other
  }
) {

  return {
    uuid,
    language,
    group: Array.isArray(groups) ? groups[0] : null,
    title,
    shortTitle,
    lead,
    phenomenonType: type,
    videoUrl,
    imageUrl,
    mediaText,
    body,
    relatedPhenomena,
    feedTag: feed_tags.map(id => ({ id, title: '...' })),
    timing,
    links: Array.isArray(links) ? links.map(({ url }) => ({ value: url })) : [],
    ...other
  }
}